import { useEffect, useMemo, useRef, useState } from "react";
import { renderEventTemplate } from "@/pages/public/templates";
import { buildPreviewEvent, type PreviewSource } from "@/lib/onboarding/buildPreviewEvent";
import { useTranslation } from "@/hooks/useTranslation";
import { TemplateFrame } from "@/components/common/TemplateFrame";
import { TemplatePageHost } from "@/lib/template-engine/TemplatePageHost";
import { isTemplatePackage } from "@/lib/template-engine/resolve";
import { cn } from "@/lib/utils";

interface LivePreviewProps {
  source: PreviewSource;
  className?: string;
  /** Height of the scrollable "phone" viewport. */
  heightClassName?: string;
  /** Section the frame scrolls to, so the answer being given is in view (§27). */
  focus?: "top" | "details" | "rsvp";
}

/**
 * JUB-154: the couple's page, rendered by the real template from the answers
 * so far (§26/§27: every answer visibly builds the page). The template lives
 * in an iframe sized like the phone (JUB-205), so its breakpoints follow the
 * mock instead of the browser window and its fixed navbar stays inside it.
 * An installed template package renders through the engine, as on the public
 * page (JUB-213); a built-in design through its TSX template.
 */
export function LivePreview({ source, className, heightClassName = "h-[640px]", focus = "top" }: LivePreviewProps) {
  const { t } = useTranslation();
  const event = useMemo(() => buildPreviewEvent(source), [source]);
  const template = event.template;
  const config: unknown = template?.config;
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const frameWindow = frameRef.current?.contentWindow;
    const doc = frameRef.current?.contentDocument;
    if (!ready || !frameWindow || !doc) return;
    const target = focus === "top" ? null : doc.getElementById(focus);
    if (!target && !(frameWindow.scrollY > 0)) return;
    const top = target ? target.getBoundingClientRect().top + frameWindow.scrollY : 0;
    frameWindow.scrollTo({ top, behavior: "smooth" });
  }, [focus, event, ready]);

  return (
    <div
      className={cn(
        "mx-auto w-full max-w-[380px] overflow-hidden rounded-[2rem] border-8 border-neutral-900/90 bg-white shadow-2xl",
        className,
      )}
      aria-label={t("onboarding.nav.previewLabel")}
      data-testid="onboarding-live-preview"
      data-design={event.theme}
    >
      {template && config ? (
        <TemplateFrame
          title={t("onboarding.nav.previewLabel")}
          className={cn("block w-full border-0", heightClassName)}
          data-testid="onboarding-live-preview-frame"
          onReady={(frame) => {
            frameRef.current = frame;
            setReady(true);
          }}
        >
          {isTemplatePackage(config) ? (
            <TemplatePageHost pkg={config} event={event} locale={source.language} />
          ) : (
            renderEventTemplate({
              event,
              config: template.config,
              locale: source.language,
              t: (key, params) => t(key, params as Record<string, string | number> | undefined),
              onShare: () => undefined,
            })
          )}
        </TemplateFrame>
      ) : null}
    </div>
  );
}
